import type { QueryClient } from '@tanstack/react-query'

import { getListConversationsApiV1ConversationsGetQueryKey } from '#/shared/api/generated/communication/theCargoCommunication'
import type {
  ConversationListItem,
  CursorPageConversationListItem,
} from '#/shared/api/generated/communication/models'
import { usPhoneDigits } from '#/shared/lib/format'

interface ConversationLookup {
  conversationId?: string
  phone?: string
}

/** A cached conversations response — either a single page or an infinite query. */
type CachedConversations =
  | { data: CursorPageConversationListItem }
  | { pages: Array<{ data: CursorPageConversationListItem }> }

/** Conversation rows from a cached entry, flattening infinite-query pages. */
function itemsOf(
  response: CachedConversations | undefined,
): ConversationListItem[] {
  if (!response) return []
  if ('pages' in response) {
    return response.pages.flatMap((page) => page.data.items ?? [])
  }
  return response.data.items ?? []
}

/**
 * Find a loaded conversation (by id or counterparty number) across every cached
 * conversations list — handles both the plain and infinite-query cache shapes.
 */
export function findCachedConversation(
  queryClient: QueryClient,
  target: ConversationLookup,
): ConversationListItem | undefined {
  const wanted = target.phone ? usPhoneDigits(target.phone) : undefined
  const entries = queryClient.getQueriesData<CachedConversations>({
    queryKey: getListConversationsApiV1ConversationsGetQueryKey(),
  })
  for (const [, response] of entries) {
    const match = itemsOf(response).find((conversation) =>
      target.conversationId
        ? conversation.id === target.conversationId
        : usPhoneDigits(conversation.to_number) === wanted,
    )
    if (match) return match
  }
  return undefined
}
